import { useEffect, useMemo, useReducer, useState } from "react"
import axios, { CancelToken } from "axios"
import pLimit from 'p-limit'
import { ApiAccess } from 'features/session/types'
import { useApiAccess } from "features/session/context"

const threadlingCount = process.env.NODE_ENV === 'production' ? 1 : 1

export type FetchListState<TResult> = {
  loading: boolean
  error: Error | null
  total: number | null
  loaded: number | null
  data: TResult | null
  refetch: () => void
}
type FetchListAction<TResult> =
  | { type: 'clear' }
  | { type: 'start' }
  | { type: 'error'; error: Error }
  | { type: 'count'; count: number }
  | { type: 'partial'; count: number }
  | { type: 'finish'; payload: TResult }

const fetchListReducer = <TResult>(
  state: FetchListState<TResult>,
  action: FetchListAction<TResult>
): FetchListState<TResult> => {
  switch (action.type) {
    case 'clear':
      return {
        ...state,
        loading: false,
        error: null,
        total: null,
        loaded: 0,
        data: null,
      }
    case 'start':
      return { ...state, loading: true, error: null }
    case 'error':
      return {
        ...state,
        loading: false,
        error: action.error,
        total: null,
        loaded: 0,
        data: null,
      }
    case 'count':
      return { ...state, total: action.count, loaded: 0 }
    case 'partial':
      return { ...state, loaded: action.count }
    case 'finish':
      return { ...state, loading: false, loaded: null, data: action.payload }
  }
}
export const useApiFetchList = <T, TResult>(
  countFunc: (
    apiAccess: ApiAccess,
    cancelToken: CancelToken
  ) => Promise<number> | number | null | undefined,
  fetchFunc: (
    apiAccess: ApiAccess,
    cancelToken: CancelToken,
    offset: number,
    limit: number
  ) => Promise<T[]> | T[] | null | undefined,
  finalyFunc: (result: T[]) => TResult,
  partialLimit: number,
  deps?: React.DependencyList
): FetchListState<TResult> => {
  const [fetchTime, setFetchTime] = useState<number>(() => (new Date()).getTime())
  const refetch = () => setFetchTime((new Date()).getTime())
  const [state, dispatch] = useReducer(fetchListReducer, {
    loading: false,
    error: null,
    total: null,
    data: null,
    refetch
  } as FetchListState<TResult>)

  const cancel = axios.CancelToken.source()
  const apiAccess = useApiAccess()

  useEffect(
    () => {
      dispatch({ type: 'clear' })

      let ignore = false
      const loadPartial = async (count: number) => {
        if (count === 0) {
          dispatch({ type: 'finish', payload: finalyFunc([]) })
          return
        }
        const maxTimes = Math.floor(count / partialLimit) + 1
        let loaded = 0
        let times = 0
        const list: T[] = []
        let promiseList = []
        const onResponse = (response: T[]) => {
          if (ignore) return

          list.push(...response)
          times = times + 1
          loaded = list.length
          console.log(`fetched: ${times}/${maxTimes}`)

          dispatch({ type: 'partial', count: list.length })
          if (times >= maxTimes) {
            finalyFunc(list)
            dispatch({ type: 'finish', payload: finalyFunc(list) })
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onError = (reason: any) => {
          if (axios.isCancel(reason) || ignore) return
          dispatch({ type: 'error', error: reason })
          ignore = true
        }
        while (times < maxTimes) {
          if (ignore) return
          const promise = fetchFunc(apiAccess, cancel.token, loaded, partialLimit)
          if (!promise) return
          if (promise instanceof Promise) {
            if (threadlingCount > 1) {
              const limit = pLimit(1)
              promiseList.push(
                limit(async () =>
                  promise.catch(onError).then((responce) => {
                    if (responce) onResponse(responce)
                    return responce
                  })
                )
              )
            } else {
              await promise.catch(onError).then((responce) => {
                if (responce) onResponse(responce)
                return responce
              })
            }
          } else {
            onResponse(promise)
          }
        }
        if (promiseList.length > 0) {
          await Promise.all(promiseList)
          promiseList = []
        }
      }
      const promise = countFunc(apiAccess, cancel.token)
      if (!promise) return

      dispatch({ type: 'start' })
      if (promise instanceof Promise) {
        promise
          .then((response) => {
            if (ignore) return
            dispatch({ type: 'count', count: response })
            loadPartial(response)
          })
          .catch((error) => {
            if (axios.isCancel(error) || ignore) return
            dispatch({ type: 'error', error: error })
          })
      } else {
        dispatch({ type: 'count', count: promise })
        loadPartial(promise)
      }
      return () => {
        ignore = true
        if (state.loading) cancel.cancel('canceled')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([
      fetchTime,
      countFunc,
      fetchFunc,
      finalyFunc,
      partialLimit,
      dispatch
      /*, apiAccess, cancel, state.loading*/
    ])
  )
  return useMemo(
    () => state as FetchListState<TResult>,
    [state]
  )
}
